// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import type { KpiBoxesProps } from '@shared/ui-kit/components';
import {
  getMape,
  sortTradesByDelta,
  unavailableTrades,
} from '@advisor-app/features/brokers/utils';
import { useTradesHistory } from '@advisor-app/features/trades-history/hooks/use-trades-history';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { deleteConfirmationPopup } from '@shared/ui-kit/utils/delete-confirmation-popup';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type TradesOperations } from '@shared/data-access/interfaces/redux/trades';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import { selectAvailableCash } from '@advisor-app/store/slices/broker';
import {
  deleteTrade,
  resetTrades,
  updateTradeAccounts,
  selectBuyTrades,
  selectBuyTradesTotalVolume,
  selectLoadingTrades,
  selectSellTrades,
  selectSellTradesTotalVolume,
  selectTraderTrades,
} from '@advisor-app/store/slices/trader';
import { equityApi } from '@advisor-app/store/api/brokerage/equity-api';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { selectClient } from '@shared/data-access/slices/clients/clients-selectors';
import { buildProposalOperations } from '@shared/advisory/proposals/utils';
import { useTranslation } from 'react-i18next';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import { isNullOrUndefined, isUndefined } from '@shared/utils';

export const useTrader = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('common');
  const availableCash = useAppSelector(selectAvailableCash);
  const { tradesHistory } = useTradesHistory();
  const client = useAppSelector(selectClient);
  const trades = useAppSelector(selectTraderTrades);
  const proposalOperations = useMemo(
    () => buildProposalOperations(trades),
    [trades],
  );
  const validProposal = useMemo(() => {
    return !trades.some(trade => {
      const isBuy = trade.operation === OperationType.BUY;
      const isTransfer = trade.operation === OperationType.TRANSFER;

      if (!isBuy && !isTransfer) return false;

      const assetDetails = isTransfer ? trade.to_asset_details : trade.columnName;

      if (!['fund', 'mutual_fund'].includes(assetDetails?.asset_type ?? '')) {
        return false;
      }

      const dfiUrl = assetDetails?.dfi_url ?? trade.dfi_url;

      return isNullOrUndefined(dfiUrl) || dfiUrl.trim() === '';
    });
  }, [trades]);
  const hasRiskProfile = useMemo(
    () => client?.risk_profile !== null || !isUndefined(client?.risk_profile),
    [client],
  );

  const isLoadingTrades = useAppSelector(selectLoadingTrades);

  const mape = useMemo(() => getMape(trades), [trades]);

  const areSomeTradeUnavailable = useMemo(
    () => unavailableTrades(trades),
    [trades],
  );

  const sortedTrades = useMemo(
    () => (mape > 5 ? sortTradesByDelta(trades) : trades),
    [mape, trades],
  );

  const currency = useAppSelector(selectAccountCurrency);

  const prevCurrency = useRef(currency);

  const [getLatestPrices] = equityApi.useLazyGetLatestPricesQuery();

  const handleDeleteTrade = useCallback(
    (ticker: string, operation: TradesOperations, index?: number) => {
      deleteConfirmationPopup(
        {
          title: t('common.delete-trade'),
          text: t('common.delete-trade-confirmation'),
        },
        isDeleted => {
          if (!isDeleted || typeof index !== 'number') return;

          const realIndex = trades.indexOf(sortedTrades[index]);
          if (realIndex !== -1) {
            dispatch(
              deleteTrade({
                index: realIndex,
                ticker,
                operation,
              }),
            );
          }
        },
        t,
      );
    },
    [dispatch, t, trades, sortedTrades],
  );

  const handleAccountChange = useCallback(
    (
      index: number,
      side: 'from' | 'to',
      iban: string,
      institution: { id: string; name: string },
    ) => {
      const realIndex = trades.indexOf(sortedTrades[index]);
      if (realIndex === -1) {
        return;
      }

      dispatch(
        updateTradeAccounts({
          index: realIndex,
          ...(side === 'from'
            ? {
                from_iban: iban,
                institution_id: institution.id,
                institution_name: institution.name,
              }
            : { to_iban: iban }),
        }),
      );
    },
    [dispatch, sortedTrades, trades],
  );

  const handleDeleteAllTrades = useCallback(() => {
    trades.length > 0
      ? deleteConfirmationPopup(
          {
            title: t('common.delete-all-trades'),
            text: t('common.delete-all-trades-confirmation'),
            confirmMessage: t('common.all-trades-deleted'),
          },
          isDeleted => {
            return isDeleted && dispatch(resetTrades());
          },
          t,
        )
      : showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('common.no-trades-to-delete'),
          buttons: {
            confirm: {
              show: true,
              text: t('common.ok'),
            },
          },
        });
  }, [trades.length, dispatch, t]);

  const buyTrades = useAppSelector(selectBuyTrades);
  const sellTrades = useAppSelector(selectSellTrades);
  const buyTradesTotal = useAppSelector(selectBuyTradesTotalVolume);
  const sellTradesTotal = useAppSelector(selectSellTradesTotalVolume);
  const investment = Math.trunc(buyTradesTotal - sellTradesTotal);
  const realizedCapitalGainsTotal = useMemo(
    () => trades.reduce((acc, curr) => acc + curr.realized_capital_gains, 0),
    [trades],
  );
  const debtAmount = useMemo(
    () => (investment > availableCash ? Math.abs(investment - availableCash) : 0),
    [availableCash, investment],
  );

  const kpiData = useMemo<KpiBoxesProps[]>(
    () => [
      {
        value: formatToMoney(investment),
        text: t('common.net-new-investment'),
        cash: availableCash,
        currency,
        icon: 'TrendingUp',
        color: 'main',
      },
      {
        value: formatToMoney(Math.trunc(buyTradesTotal)),
        text: t('common.buy'),
        numberPositions: buyTrades.length,
        icon: 'CircleArrowUp',
        color: 'success',
      },
      {
        value: formatToMoney(Math.trunc(sellTradesTotal)),
        text: t('common.sell'),
        numberPositions: sellTrades.length,
        icon: 'CircleArrowDown',
        color: 'danger',
      },
      {
        value: formatToMoney(Math.trunc(realizedCapitalGainsTotal)),
        text: t('common.realized-gains'),
        popupName: 'kpis.realizedGains',
        icon: 'DollarSign',
        color: 'success',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      investment,
      availableCash,
      buyTradesTotal,
      buyTrades.length,
      sellTradesTotal,
      sellTrades.length,
      realizedCapitalGainsTotal,
      currency,
      i18n.language,
    ],
  );

  useEffect(() => {
    /* istanbul ignore next */
    if (areSomeTradeUnavailable || currency !== prevCurrency.current) {
      prevCurrency.current = currency;

      getLatestPrices(trades, true).catch(err =>
        console.error('[useTrader]: ', err),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areSomeTradeUnavailable, currency]);

  return {
    kpiData,
    handleDeleteTrade,
    handleAccountChange,
    handleDeleteAllTrades,
    tradesHistory,
    sortedTrades,
    trades,
    mape,
    areSomeTradeUnavailable,
    debtAmount,
    availableCash,
    isLoadingTrades,
    proposalOperations,
    hasRiskProfile,
    validProposal,
  };
};
